import { useEffect, useState } from 'react'
import { COLORS } from '../../lib/theme'
import { LocaleDropdown, type LocaleOption } from './LocaleDropdown'
import { useCloseOnEscape } from './topbarChrome'

export type { LocaleOption }

export interface LocaleSelection {
  country: string
  language: string
}

export interface LocationModalLabels {
  title?: string
  country?: string
  language?: string
  cancel?: string
  apply?: string
  close?: string
}

/** The markets wehunt.app offers, in the order the site lists them. */
export const DEFAULT_COUNTRIES: LocaleOption[] = [
  { id: 'GLOBAL', label: 'Global', flag: 'GLOBAL' },
  { id: 'FI', label: 'Finland', flag: 'FI' },
  { id: 'SE', label: 'Sweden', flag: 'SE' },
  { id: 'DE', label: 'Germany', flag: 'DE' },
  { id: 'FR', label: 'France', flag: 'FR' },
  { id: 'DK', label: 'Denmark', flag: 'DK' },
  { id: 'NO', label: 'Norway', flag: 'NO' },
]

export const DEFAULT_LANGUAGES: LocaleOption[] = [
  { id: 'en', label: 'English', flag: 'GB' },
  { id: 'sv', label: 'Svenska', flag: 'SE' },
  { id: 'fi', label: 'Suomi', flag: 'FI' },
  { id: 'nb', label: 'Norsk', flag: 'NO' },
  { id: 'da', label: 'Dansk', flag: 'DK' },
  { id: 'de', label: 'Deutsch', flag: 'DE' },
  { id: 'fr', label: 'Français', flag: 'FR' },
]

/**
 * "Select your location" — the site puts country and language behind one confirm
 * step rather than switching on click, so a mis-tap does not reload the page
 * into another market. Kept as a modal for the same reason.
 *
 * A shell that has no notion of a market (the web app: the shop chooses the
 * store, this app only speaks a language) passes `showCountry={false}` and gets
 * the language picker alone under the same chrome.
 *
 * Unlike the menu and search surfaces this uses a plain 50% scrim with no blur,
 * which is what the site does here.
 */
export function LocationModal({
  open,
  onClose,
  onApply,
  selection,
  countries = DEFAULT_COUNTRIES,
  languages = DEFAULT_LANGUAGES,
  showCountry = true,
  labels,
}: {
  open: boolean
  onClose: () => void
  onApply?: (next: LocaleSelection) => void
  selection: LocaleSelection
  countries?: LocaleOption[]
  languages?: LocaleOption[]
  showCountry?: boolean
  labels?: LocationModalLabels
}) {
  const [draft, setDraft] = useState(selection)

  useCloseOnEscape(open, onClose)

  // Reopening starts from what is actually applied, discarding an abandoned edit.
  useEffect(() => {
    if (open) setDraft(selection)
  }, [open, selection])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={labels?.title ?? 'Select country and language'}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        padding: 16,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          background: COLORS.surfaceContainerLowest,
          color: COLORS.onSurface,
          borderRadius: 12,
          boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
            padding: '24px 24px 16px',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, lineHeight: 1.5 }}>
            {labels?.title ?? 'Select your location'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={labels?.close ?? 'Close'}
            style={{
              padding: 4,
              border: 'none',
              borderRadius: 4,
              background: 'transparent',
              color: COLORS.onSurfaceVariant,
              fontSize: 24,
              lineHeight: 1,
              cursor: 'pointer',
              transition: 'color 0.15s',
            }}
          >
            ×
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '0 24px 24px' }}>
          {showCountry && (
            <LocaleDropdown
              label={labels?.country ?? 'Country'}
              options={countries}
              value={draft.country}
              onChange={(country) => setDraft((d) => ({ ...d, country }))}
            />
          )}
          <LocaleDropdown
            label={labels?.language ?? 'Language'}
            options={languages}
            value={draft.language}
            onChange={(language) => setDraft((d) => ({ ...d, language }))}
          />
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            padding: '16px 24px',
            borderTop: `1px solid ${COLORS.surfaceContainerHighest}`,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              borderRadius: 6,
              border: `1px solid ${COLORS.outlineVariant}`,
              background: 'transparent',
              color: COLORS.onSurfaceVariant,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
          >
            {labels?.cancel ?? 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => {
              onApply?.(draft)
              onClose()
            }}
            style={{
              padding: '8px 24px',
              borderRadius: 6,
              border: 'none',
              background: COLORS.primary,
              color: COLORS.onPrimary,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
              transition: 'background-color 0.15s',
            }}
          >
            {labels?.apply ?? 'Apply'}
          </button>
        </div>
      </div>
    </div>
  )
}
